import {POPUP} from "back/commons/widgets/popups";
import {ESrcRights} from 'lib/commons/security';
import {EItResp, EItStatus} from "lib/wsp/item";

/** cf com.scenari.src.ISrcContent */
export const enum ESrcSt {
	none = -1,
	conflict = -2,
	file = 1,
	folder = 2
}

/** Refs vers une uri et/ou un id. */
export type srcUri = string;
export type srcId = string;
export type srcRef = srcUri | srcId;

export interface JSrcIdent {
	srcUri?: srcUri
	srcId?: srcId
	itSubItem?: JSubItem
}

export interface JSrcFields extends JSrcIdent {
	//com.scenari.src.feature.fields.SrcFeatureFields
	srcNm?: string
	srcSt?: ESrcSt
	srcDt?: number
	srcStamp?: string
	srcTreeDt?: number
	srcSi?: number
	srcTy?: string //ContentType
	srcRi?: ESrcRights
	srcRoles?: string[]
	srcUser?: string
	srcLiveUri?: string
	srcTrashed?: boolean
	metaComment?: string
	metaFlag?: number
	//com.scenari.src.feature.drv.SrcFeatureDrv
	drvState?: EDrvState
	drvAxisDefCo?: string | null
	//com.scenari.src.feature.drf.SrcFeatureDrf
	drfState?: EDrfState
	//eu.scenari.wsp.item.IItemDataKeys
	itTi?: string
	itSt?: EItStatus // @see eu.scenari.wsp.item.IItemDef
	itSgn?: string
	itModel?: string
	itFullUriInOwnerWsp?: string
	itSubItems?: JSubItem[]
	/* propriétés DES subItems ancêtres DU subItem pointé */
	itSubItemAnc?: JSubItem[]
	//com.scenari.src.feature.responsibility.SrcFeatureResponsibility
	rspUsrs?: JInvolvedUser[]
	rspSt?: EItResp
	//com.scenari.src.feature.tasks.SrcFeatureTasks
	tkPending?: boolean
	tkPendingCount?: number
	tkForthcoming?: boolean
	tkForthcomingCount?: number
	//com.scenari.src.act.IAct
	actStage?: EActStage
	actTi?: string
	actCts?: JActTouchedContent[]
	//com.scenari.src.feature.lifecycle.SrcFeatureLifeCycle
	lcSt?: string
	lcDt?: number
	lcBy?: string
	//com.scenari.src.feature.tasks.ISrcTask
	tkDeadline?: number
	tkCompletedDt?: number
	tkCompletedBy?: string
	//eu.scenari.wsp.provider.IReposSrcNode
	wspOwner?: string
	// @DEPRECATED com.scenari.src.feature.alternateurl.SrcFeatureAlternateUrl
	// préférer l'appel de "SvcWspSrcSender" avec @format=filePath
	//srcFilePath?: string
}

export interface JSubItem {
	id: string
	ti?: string
	mo?: string
	subItems?: JSubItem[]
}

export interface JSrcFieldsTree extends JSrcFields {
	ch?: JSrcFieldsTree[]
}

/** Utilisé pour les entrées d'historique d'un noeud ET les noeuds fils en corbeille*/
export interface JSrcHistNodes extends JSrcFields {
	liveNode: JSrcFields
	histNodes: JSrcFields[]
}

export interface JInvolvedUser {
	usr: string,
	resp: string[]
}

export interface JActTouchedContent {
	srcId: srcId
}

export type ESrcField = keyof JSrcFields;

/** cf com.scenari.src.feature.drv.SrcFeatureDrv */
export type EDrvState = 'erased' | 'notOverriden' | 'notOverridenDone' | 'createdDone' | 'overridenDone' | 'createdNew' | 'overridenNew' | 'notOverridenDirty' | 'createdDirty' | 'overridenDirty' | 'createdNotComputed' | 'notComputed';

/** cf com.scenari.src.feature.drf.SrcFeatureDrf */
export type EDrfState = 'erased' | 'notOverriden' | 'overriden' | 'created' | 'createdDeleted' | 'notComputed';

/** cf com.scenari.src.act.IAct.ActStage */
export type EActStage = 'pending' | 'forthcoming' | 'completed';

/**
 * Propriétés et fonctions relatives aux responsabilities
 */
export namespace RESPS {
	/**
	 * Transforme un JInvolvedUser[] en liste de users par resp
	 */
	export function toRespsList(involvedUsers?: JInvolvedUser[]): JRespsList | null {
		if (!involvedUsers) return null;
		const list: JRespsList = {};
		involvedUsers.forEach((entry) => {
			if (entry.resp)
				entry.resp.forEach((resp) => {
					if (!list[resp]) list[resp] = [];
					if (entry.usr) list[resp].push(entry.usr);
				})
		});
		return list;
	}

	type JRespsList = { [key: string]: string[] }
}


/**
 * Propriétés et fonctions relatives à l'API Src (uri, paths, id...).
 */
export namespace SRC {

	/**
	 * Uri racine d'un arbre de source.
	 */
	export const URI_ROOT = "";

	export function srcRef(ident: JSrcIdent): srcRef {return ident.srcId || ident.srcUri}

	/**
	 * Ajoute un élément fils à une uri et retourne la nouvelle Uri.
	 */
	export function addLeafToUri(uriParent: string, leafName: string): string {
		if (uriParent == null) throw Error("SRC.addLeafToUri : uriParent is null.");
		return uriParent + "/" + leafName;
	}

	/**
	 * Retourne l'Uri parent d'une uri ou null si uri est la racine "".
	 */
	export function extractUriParent(uri: string): string {
		if (uri == null) throw Error("src.extractUriParent : uri is null.");
		if (uri == "") return null;
		const idx = uri.lastIndexOf("/");
		if (idx <= 0) return "";
		return uri.substring(0, idx);
	}

	/**
	 * Retourne l'élément terminal d'une Uri.
	 */
	export function extractLeafFromUri(uri: string): string {
		if (uri == null) throw Error("src.extractLeafFromUri : uri is null.");
		const idx = uri.lastIndexOf("/") + 1;
		//let idx2 = uri.indexOf("#", idx);
		//if(idx<=0) return idx2<0 ? uri : uri.substring(0, idx2);
		//if(idx2>=0) uri.substring(idx, idx2);
		return uri.substring(idx);
	}

	/**
	 * ancestorUri est-elle un ancêtre de uri.
	 * Si ancestorUri==uri retourne false.
	 */
	export function isSubUri(ancestorUri: string, uri: string): boolean {
		return uri.startsWith(ancestorUri) && uri.charAt(ancestorUri.length) === "/";
	}

	/**
	 * ancestorUri est-elle un ancêtre de uri.
	 * Si ancestorUri==uri retourne true.
	 */
	export function isSubUriOrEqual(ancestorUri: string, uri: string): boolean {
		const parentLen = ancestorUri.length;
		if (parentLen === uri.length) return ancestorUri === uri;
		return uri.startsWith(ancestorUri) && uri.charAt(parentLen) === "/";
	}

	/**
	 * parentUri est-elle l'uri parent de uri.
	 */
	export function isParentUri(parentUri: string, uri: string): boolean {
		const parentLen = parentUri.length;
		if (uri.length > parentLen && uri.lastIndexOf(parentUri, 0) == 0) {
			return uri.charAt(parentLen) === "/" && uri.indexOf("/", parentLen + 1) < 0;
		}
		return false;
	}

	/**
	 * Evalue l'extension, non sensible à la casse.
	 * @param fileName Ex: "image.JpEg"
	 * @param ext Ex: "png", "JPEG", ".png"
	 */
	export function matchExt(fileName: string, ext: string): boolean {
		const startExt = fileName.length - ext.length;
		if (ext.charAt(0) !== '.' && fileName.charAt(startExt - 1) !== '.') return false;
		return fileName.substring(startExt).toLowerCase() === ext.toLowerCase();
	}

	/**
	 * Evalue si une chaine de caractère est une part d'uri valide (space, code item ou name de res).
	 * @param fromForNotif Si resnseigné une notification sera affichée en cas de nom invalide.
	 */
	export function isValidPartUri(name: string, fromForNotif?: Node): true | string {
		let r: true | string = true;
		//Caractères interdits.
		if (/[+$\\\/><|?:*#"~]/.test(name)) r = "Le nom ne doit comporter aucun des caractères suivants : $ + \\ / > < | ? : * # \" ~";
		//.. interdit
		if (r === true && /\.\./.test(name)) r = "Le nom ne doit pas comporter deux points successifs.";
		//Ne peut commencer par un "."
		if (r === true && name.charAt(0) == ".") r = "Le nom ne peut pas commencer par un point.";
		if (fromForNotif && r !== true) POPUP.showNotifWarning(r, fromForNotif);
		return r;
	}

	export function filterPartUri(name: string): string {
		return name.replace(/[+$\\\/><|?:*#"~\s]/g, "");
	}

	/**
	 * Prefix pour les srcRef de type Id.
	 */
	export const ID_PREFIX = "id:";

	/**
	 * Une srcRef est-elle de nature Id ?
	 */
	export function isSrcId(srcRef: srcRef): srcRef is srcId {
		return srcRef ? srcRef.lastIndexOf(ID_PREFIX, 0) === 0 : false;
	}

	/**
	 * Prefix pour les srcUri de type new.
	 */
	export const NEW_PREFIX = "new:";

	/**
	 * Cette srcUri représente-t-elle une uri en création.
	 */
	export function isNewSrcUri(srcUri: srcUri): boolean {
		return srcUri ? srcUri.lastIndexOf(NEW_PREFIX, 0) === 0 : false;
	}

	/**
	 * Crée une srcUri pour un contenu en création (ie inconnu du serveur).
	 * Schéma : 'new:' itModel ':' uniqueId
	 */
	export function createNewSrcUri(itModel: string): srcUri {
		return `${NEW_PREFIX}${itModel}:${Math.random().toString(36).substr(2)}`
	}

	export function extractItModelFromNewSrcUri(srcUri: srcUri): string | null {
		if (!isNewSrcUri(srcUri)) return null;
		const end = srcUri.indexOf(':', NEW_PREFIX.length);
		return end > 0 ? srcUri.substring(NEW_PREFIX.length, end) : srcUri.substring(NEW_PREFIX.length);
	}

	/**
	 * Résolution des alias des srcFields et dédoublonnement.
	 */
	export function resolveAliasSrcFields(fields: string[]): Set<ESrcField> {
		const set = new Set<ESrcField>();
		for (let k of fields) {
			if (k in aliasSrcFields) for (let f of aliasSrcFields[k]) set.add(f);
			else set.add(k as ESrcField);
		}
		return set;
	}

	const aliasSrcFields: Dict<ESrcField[]> = {
		"sdFs": ["srcUri", "srcSt", "srcDt", "srcRi", "srcRoles"],
		"sdOdb": ["srcUri", "srcSt", "srcDt", "srcRi", "srcRoles", "srcId", "srcStamp", "srcUser"],
		"item": ["itTi", "itSt", "itSgn", "itModel"],
	}

	/**
	 * Comparator dédié aux uri (ie un arbre de fragments sparés par / ) qui correspond à l'ordre logique d'affichage d'un arbre.
	 * TRES important pour algo d'affichage d'un arbre déployé AVEC pagination.
	 *
	 * cf java com.scenari.src.search.UriTreeComparator
	 */
	export function sortSrcUriTree(p1: string, p2: string): number {
		const s1 = p1.length;
		const s2 = p2.length;
		for (let i = 0, m = Math.min(s1, s2); i < m; i++) {
			const c1 = p1.codePointAt(i);
			const c2 = p2.codePointAt(i);
			if (c1 === c2) continue;
			if (c1 < 127) {
				if (c2 < 127) return URITREE_ASCII_ORDER[c1] - URITREE_ASCII_ORDER[c2];
				return -1;
			} else {
				return c1 - c2;
			}
		}
		if (s1 === s2) return 0;
		return s1 - s2;
	}

	export function extractIdFromSrcId(srcId: srcId): string {
		return srcId.substr(3);
	}

	export function refUri2AgtBagId(srcUri: srcUri): string {
		return srcUri.replace(/\//g, "\\") + ";";
	}

	export function refUri2AgtPath(srcUri: srcUri, axis?: String, role?: String, nodeId?: String): string {
		let vPath = "@" + refUri2AgtBagId(srcUri);
		if (nodeId) vPath += nodeId;
		if (role) vPath += "_" + role;
		if (axis) vPath += "_A" + axis;
		return vPath;
	}


}

const URITREE_ASCII_ORDER = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 33, 32, 61, 62, 63, 64, 65, 66, 67,
	68, 69, 70, 48, 49, 50, 51, 52, 53, 54, 71, 73, 75, 77, 79, 81, 83, 85, 87, 89, 91, 93, 95, 97, 99, 101, 103, 105, 107, 109, 111, 113, 115, 117, 119, 121, 55, 56, 57, 58, 59, 60, 72, 74, 76, 78, 80, 82, 84, 86, 88, 90, 92, 94, 96, 98, 100, 102,
	104, 106, 108, 110, 112, 114, 116, 118, 120, 122, 123, 124, 125, 126];
